import { STORAGE_THEME } from "./i18n/copy";

export type Theme = "light" | "dark";

function systemTheme(): Theme {
  // Day/night on the plaza is a stylistic choice, not an OS mirror: the scene
  // content (console, exhibits) is authored for the dark look and the viewer
  // always lands on it. A stored choice still wins (see detectTheme below).
  return "dark";
}

export function detectTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_THEME);
  if (stored === "light" || stored === "dark") return stored;
  return systemTheme();
}

function updateIcons(theme: Theme): void {
  const moon = document.getElementById("icon-moon");
  const sun = document.getElementById("icon-sun");
  // Explicit mutually exclusive visibility (prevents both icons stacking)
  if (moon instanceof SVGElement || moon instanceof HTMLElement) {
    const showMoon = theme === "light";
    moon.toggleAttribute("hidden", !showMoon);
    moon.classList.toggle("is-active", showMoon);
  }
  if (sun instanceof SVGElement || sun instanceof HTMLElement) {
    const showSun = theme === "dark";
    sun.toggleAttribute("hidden", !showSun);
    sun.classList.toggle("is-active", showSun);
  }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", theme === "dark" ? "#05070c" : "#eef1f7");
}

function applyThemeSync(theme: Theme): void {
  // Set on <html> so CSS vars resolve before paint; body.dark kept for compat
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.classList.toggle("light", theme === "light");
  document.body.classList.toggle("dark", theme === "dark");
  document.body.classList.toggle("light", theme === "light");
  // Ensure body background uses token immediately
  document.body.style.backgroundColor = "";
  updateIcons(theme);
}

async function withViewTransition(apply: () => void): Promise<void> {
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { finished: Promise<void> };
  };

  if (
    typeof doc.startViewTransition === "function" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    try {
      await doc.startViewTransition(apply).finished;
      return;
    } catch {
      // fall through
    }
  }
  apply();
}

export async function applyTheme(theme: Theme): Promise<void> {
  await withViewTransition(() => applyThemeSync(theme));
  localStorage.setItem(STORAGE_THEME, theme);
  // Notify ink canvas / other listeners after CSS vars have settled
  document.dispatchEvent(
    new CustomEvent("doorpost:theme", { detail: { theme } }),
  );
}

export async function toggleTheme(current: Theme): Promise<Theme> {
  const next: Theme = current === "dark" ? "light" : "dark";
  await applyTheme(next);
  return next;
}

export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    // Only follow OS when user has not locked a preference
    if (localStorage.getItem(STORAGE_THEME)) return;
    onChange(systemTheme());
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}

/** Apply theme ASAP before first paint helpers (no transition). */
export function bootTheme(theme: Theme): void {
  applyThemeSync(theme);
  localStorage.setItem(STORAGE_THEME, theme);
}
